import React from 'react';
import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';
import { PapiComment } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { getModuleCommentableUrl } from '@wix/da-shared-react/pkg/utils/commentable';
import { CommentableItem } from '../../../types/comments';
import Profile from './Profile';
import GenericCommentable from './Generic';
import Deviation from './Deviation';
import ConversationTopic from './ConversationTopic';

export interface Props {
  item: CommentableItem;
  parent?: PapiComment;
}

export const Commentable: React.FC<Props> = ({
  item: {
    profile,
    deviation,
    collection,
    forum,
    groupnote,
    conversationTopic,
    announcement,
    module: bppModule,
  },
  item,
  parent,
}) => {
  const { t } = useTranslation();
  const dataKey =
    item.dataKey === 'conversation_topic' ? 'conversationTopic' : item.dataKey;
  if (!dataKey || !item[dataKey]) {
    // something is very fucky, just bail
    return null;
  }
  switch (dataKey) {
    case 'profile':
      return <Profile profile={profile!} />;
    case 'deviation':
      return <Deviation deviation={deviation!} />;
    case 'collection':
      return (
        <GenericCommentable
          title={collection!.name}
          url={Url.userGalleryLink({
            username: collection!.owner.username,
            gallectionType: collection!.type as 'gallery' | 'collection',
            folderId: collection!.folderId,
            folderName: collection!.name,
          })}
          author={collection!.owner}
        />
      );
    case 'forum':
      return (
        <GenericCommentable
          title={forum!.subject}
          author={forum!.author}
          url={Url.forumLink(forum!.forumPath, forum!.threadId)}
        />
      );
    case 'groupnote':
      return (
        <GenericCommentable
          title={groupnote!.subject}
          author={groupnote!.sender}
          url={groupnote?.url}
        />
      );
    case 'conversationTopic':
      return <ConversationTopic topic={conversationTopic!} />;
    case 'announcement':
      return <ConversationTopic topic={announcement!} />;
    case 'module':
      // TODO: this is less than perfect, but for now shall do
      // eventually, if we want parity with green site, we will need to
      // add a more complex component to deal with all the module types
      const moduleData = bppModule && bppModule[camelCase(bppModule.dataKey)];
      if (moduleData) {
        return (
          <GenericCommentable
            title={t([
              `commentable.title.correspondence.${bppModule!.dataKey}`,
              'commentable.title.correspondence.unknown',
            ])}
            url={getModuleCommentableUrl(moduleData, parent)}
            author={moduleData.bppInstigator}
          />
        );
      }
      break;
    default:
  }

  return <h1>{t('commentable.header.unknown')}</h1>;
};
Commentable.displayName = 'Commentable';

export default Commentable;
